import { useEffect, useRef, useState } from "react";

// Models
import type ICarouselContentDTO from "../../../../Models/DTOs/ICarouselContentDTO";
import type SeriesDTO from "../../../../Models/DTOs/SeriesDTO";
import type VideoDTO from "../../../../Models/DTOs/VideoDTO";

// Api
import { GetSeriesByCategory } from "../../../../Api/Series"; // adjust path
import { GetVideosBySeries } from "../../../../Api/Video";

// Helpers
import {transformSeriesToCarouselContent} from "../../../../Helpers/DataConversion/ConvertToCarouselContentItem";

type EnrichedCoreCollectionItem = ICarouselContentDTO & {
    LatestVideo?: VideoDTO | null;
    LatestVideoEpoch?: number;
};

function useFetchCoreCollection() {
    const
        [items, setItems] = useState<EnrichedCoreCollectionItem[]>([]),
        [loading, setLoading] = useState(false),
        [error, setError] = useState("");

    // session cache: seriesId -> { video-single, epoch }
    const latestCacheRef = useRef<Map<number, { video: VideoDTO | null; epoch?: number }>>(
        new Map()
    );

    useEffect(() => {
        let isCancelled = false;
        const abort = new AbortController();

        setLoading(true);
        setError("");

        const CONCURRENCY = 6;

        const withConcurrency = async <T, R>(
            list: T[],
            limit: number,
            task: (item: T, index: number) => Promise<R>
        ): Promise<R[]> => {
            const results: R[] = new Array(list.length);
            let i = 0;

            const workers = new Array(Math.min(limit, list.length))
                .fill(null)
                .map(async () => {
                    while (!isCancelled) {
                        const idx = i++;
                        if (idx >= list.length) break;

                        try {
                            results[idx] = await task(list[idx], idx);
                        } catch {
                            // swallow; leave undefined at idx
                        }
                    }
                });

            await Promise.all(workers);
            return results;
        };

        const toEpoch = (iso?: string | null): number | undefined => {
            if (!iso) return undefined;
            const t = new Date(iso).getTime();
            return Number.isFinite(t) ? t : undefined;
        };

        (async () => {
            try {
                // 1) fetch series list
                const seriesWrap = await GetSeriesByCategory({
                    categoryId: 13,
                    signal: abort.signal,
                });
                if (isCancelled) return;

                const seriesList: SeriesDTO[] = seriesWrap?.items ?? [];

                // 2) build jobs to fetch latest published episode per series
                const jobs = seriesList.map((s) => async (): Promise<{ id: number; video: VideoDTO | null; epoch?: number }> => {
                    const id = s?.Id;

                    if (!id) return { id: 0, video: null, epoch: undefined };

                    // cache hit?
                    const cached = latestCacheRef.current.get(id);
                    if (cached) return { id, video: cached.video, epoch: cached.epoch };

                    try {
                        const vids = await GetVideosBySeries({
                            seriesId: id,
                            take: 1,
                            skip: 0,
                            order: "desc",
                            publishedOnly: true,
                            signal: abort.signal,
                        });

                        const latest = vids?.items?.[0] ?? null;
                        const epoch = toEpoch(latest?.Datetime);

                        latestCacheRef.current.set(id, { video: latest, epoch });
                        return { id, video: latest, epoch };
                    } catch {
                        latestCacheRef.current.set(id, { video: null, epoch: undefined });
                        return { id, video: null, epoch: undefined };
                    }
                });

                const results = await withConcurrency(jobs, CONCURRENCY, (fn) => fn());
                if (isCancelled) return;

                // 3) index latest video-single results by seriesId
                const byId = new Map<number, { video: VideoDTO | null; epoch?: number }>();
                for (const r of results) {
                    if (!r || !r.id) continue;
                    byId.set(r.id, { video: r.video, epoch: r.epoch });
                }

                // 4) transform series -> carousel content, enrich with latest video-single + epoch
                const enriched: EnrichedCoreCollectionItem[] = seriesList.map((s) => {
                    const base = transformSeriesToCarouselContent(s);

                    const r = byId.get(s.Id);
                    return {
                        ...base,
                        LatestVideo: r?.video ?? null,
                        LatestVideoEpoch: r?.epoch,
                    };
                });

                // 5) sort by latest episode datetime (newest first), series with no episodes go last
                enriched.sort((a, b) => (b.LatestVideoEpoch ?? -1) - (a.LatestVideoEpoch ?? -1));

                setItems(enriched);
            } catch (err) {
                if (!isCancelled) {
                    console.error(err);
                    setError("Failed to fetch series.");
                }
            } finally {
                if (!isCancelled) setLoading(false);
            }
        })();

        return () => {
            isCancelled = true;
            abort.abort();
        };
    }, []);

    return { items, loading, error };
}

export default useFetchCoreCollection;
